import { recordSentry } from "./recordSentry";
import { stripNullIndexKeys } from "./stripNullIndexKeys";

/**
 * The attributes `CreateFieldInstanceInput` accepts (see src/API.ts). AppSync rejects a batch as a whole when any item
 * carries a key outside this list, so display-only properties such as `fieldTemplate`, `altInstance` or `tableFields`
 * that ride along on in-memory field instances must never reach the resolver.
 */
export const FIELD_INSTANCE_INPUT_KEYS = [
	"id",
	"formInstance",
	"formGroup",
	"fieldTemplateId",
	"value",
	"revisions",
	"authors",
	"altInstanceId",
	"tableField",
	"fieldsOrder",
	"sortingPriority",
	"completed",
	"group",
	"imageId",
	"createdAt",
	"updatedAt",
	"editing",
	"notes",
	"lastAuthor",
];

/**
 * Returns a copy of `item` holding only the attributes the create input accepts, with null index keys removed. Keys
 * that had to be dropped are reported once per call so the code that attached them can be fixed.
 * @param items The documents about to be sent to batchAddFieldInstances
 */
export function toFieldInstanceInputs<T extends Record<string, unknown>>(items: T[]): T[] {
	const dropped = new Set<string>();
	const inputs = items.map((item) => {
		const copy: Record<string, unknown> = {};
		for (const key of Object.keys(item)) {
			if (FIELD_INSTANCE_INPUT_KEYS.includes(key)) copy[key] = item[key];
			else dropped.add(key);
		}
		return stripNullIndexKeys(copy as T);
	});
	if (dropped.size > 0) {
		recordSentry({
			error: new Error(`Field instance batch carried attributes the API does not accept: ${[...dropped].join(", ")}`),
			msg: "Unexpected attributes stripped from field instance batch",
			additionalData: { dropped: [...dropped], count: items.length, formGroup: items[0]?.formGroup },
		});
	}
	return inputs;
}
